/**
 * Next.js throws "The router state header was sent but could not be parsed"
 * (HTTP 500) when `Next-Router-State-Tree` is present but is not a flight
 * router state. Full page loads never send the header. Client navigations
 * send a URL-encoded JSON tree; probes and truncated proxies send junk.
 *
 * The check mirrors `parseAndValidateFlightRouterState` in Next 15: size cap,
 * `decodeURIComponent` + `JSON.parse`, then the flight-router-state tuple.
 * Callers delete the header when it fails so Next renders from the URL
 * instead of answering 500.
 */

export const NEXT_ROUTER_STATE_TREE_HEADER = 'next-router-state-tree';

/** Same cap as Next: ~40kb, about 2,000 nested segment states. */
const ROUTER_STATE_HEADER_MAX_LENGTH = 20 * 2000;

const DYNAMIC_PARAM_TYPES = new Set(['c', 'ci', 'oc', 'd', 'di']);
const REFRESH_MARKERS = new Set(['refetch', 'refresh', 'inside-shared-layout', 'metadata-only']);

/** Deep enough for locale + nested layouts. Deeper trees are treated as junk. */
const MAX_ROUTER_STATE_DEPTH = 32;

function isSegment(value: unknown): boolean {
  if (typeof value === 'string') return true;
  if (!Array.isArray(value) || value.length !== 3) return false;
  return (
    typeof value[0] === 'string' &&
    typeof value[1] === 'string' &&
    typeof value[2] === 'string' &&
    DYNAMIC_PARAM_TYPES.has(value[2])
  );
}

function isFlightRouterState(value: unknown, depth: number): boolean {
  if (depth > MAX_ROUTER_STATE_DEPTH) return false;
  if (!Array.isArray(value) || value.length < 2 || value.length > 5) return false;
  if (!isSegment(value[0])) return false;

  const parallelRoutes = value[1];
  if (
    parallelRoutes === null ||
    typeof parallelRoutes !== 'object' ||
    Array.isArray(parallelRoutes)
  ) {
    return false;
  }
  for (const child of Object.values(parallelRoutes as Record<string, unknown>)) {
    if (!isFlightRouterState(child, depth + 1)) return false;
  }

  if (value.length > 2 && value[2] != null && typeof value[2] !== 'string') return false;
  if (
    value.length > 3 &&
    value[3] != null &&
    (typeof value[3] !== 'string' || !REFRESH_MARKERS.has(value[3]))
  ) {
    return false;
  }
  if (value.length > 4 && typeof value[4] !== 'boolean') return false;
  return true;
}

/** True when Next would accept this single header value. */
export function isParseableRouterStateHeader(value: string): boolean {
  if (value.length > ROUTER_STATE_HEADER_MAX_LENGTH) return false;

  let state: unknown;
  try {
    state = JSON.parse(decodeURIComponent(value));
  } catch {
    return false;
  }
  return isFlightRouterState(state, 0);
}

/**
 * A copy of `headers` with `Next-Router-State-Tree` removed when Next would
 * throw while parsing it. Returns null when the request should be forwarded
 * unchanged (header absent or valid).
 */
export function headersWithoutUnparseableRouterState(headers: Headers): Headers | null {
  const value = headers.get(NEXT_ROUTER_STATE_TREE_HEADER);
  if (value == null || isParseableRouterStateHeader(value)) return null;

  const next = new Headers(headers);
  next.delete(NEXT_ROUTER_STATE_TREE_HEADER);
  return next;
}
